import { Emit_main } from "../navigation";
import { FC, Fragment, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMeta, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faCopyright, faPeopleGroup, faCheck } from "@fortawesome/free-solid-svg-icons";
import { Eres_card, Eres_wizard_button } from "../controls";

interface IntegrationStepProps {
  stepNumber: number;
  title: string;
  isCompleted: boolean;
  icon: any;
  brandColor: string;
  details: string;
  isLast: boolean;
}

interface IntegrationStatus {
  meta: boolean;
  linkedIn: boolean;
  tikTok: boolean;
  brandCreated: boolean;
  audienceCreated: boolean;
}

function checkUserIntegrations(): IntegrationStatus {
  return {
    meta: true,
    linkedIn: true,
    tikTok: true,
    brandCreated: false,
    audienceCreated: false,
  };
}

const IntegrationStep: FC<IntegrationStepProps> = ({ stepNumber, title, isCompleted, icon, brandColor, details, isLast }) => {
  const circleBgClass = isCompleted ? 'tw-bg-big-stone-400' : 'tw-bg-big-stone-200';
  const circleBorderClass = isCompleted ? 'tw-border-big-stone-600' : 'tw-bg-stone-200';

  return (
    <li className="tw-flex tw-items-start tw-relative tw-mb-10">
      <div className={`tw-absolute tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 ${circleBgClass} tw-rounded-full tw-left-4 tw-border-2 ${circleBorderClass}`}>
        <span className={`${isCompleted ? 'tw-text-big-stone-50' : 'tw-text-black'}`}>{stepNumber}</span>
      </div>
      {!isLast && (
        <svg className="tw-absolute tw-left-8 tw-top-8" width="2" height="150%" fill="none">
          <path d={`M 0 0 L 0 142`} stroke={`${isCompleted ? '#3d608c' : '#d5d9e2'}`} strokeWidth="4"/>
        </svg>
      )}
      <div className="tw-pl-16 tw-flex">
        <FontAwesomeIcon icon={icon} size='2x' style={{ color: isCompleted ? brandColor : '#aaa' }} className="tw-mr-4" aria-hidden="true"/>
        <div>
          <h3 className="tw-font-medium tw-leading-tight">
            {title}
            {isCompleted && <FontAwesomeIcon icon={faCheck} className="tw-text-green-500 tw-ml-2" aria-hidden="true"/>}
          </h3>
          <p className="tw-text-sm">{details}</p>
        </div>
      </div>
    </li>
  );
};

const UserIntegrationProgress: FC<{ integrationStatus: IntegrationStatus }> = ({ integrationStatus }) => {
  return (
    <Eres_card>
      <ol className="tw-relative tw-text-gray-500 tw-p-8">
        <IntegrationStep
          stepNumber={1}
          title="Meta Integration"
          isCompleted={integrationStatus.meta}
          icon={faMeta}
          brandColor="#0081FB"
          details="Enables publishing ads via a business manager, Facebook page, Instagram profile, ad account, or pixel."
          isLast={false}
        />
        <IntegrationStep
          stepNumber={2}
          title="LinkedIn Integration"
          isCompleted={integrationStatus.linkedIn}
          icon={faLinkedin}
          brandColor="#0077b5"
          details="Allows for ad publication through a business manager, organization, ad account, or insight tag."
          isLast={false}
        />
        <IntegrationStep
          stepNumber={3}
          title="TikTok Integration"
          isCompleted={integrationStatus.tikTok}
          icon={faTiktok}
          brandColor="#000000"
          details="Facilitates publishing ads via a business manager, identity, profile, ad account, or pixel."
          isLast={false}
        />
        <IntegrationStep
          stepNumber={4}
          title="Brand Creation"
          isCompleted={integrationStatus.brandCreated}
          icon={faCopyright}
          brandColor="#0081FB"
          details="Streamlines ad campaign setup, making it simpler to select the right page, ad account, or profiles."
          isLast={false}
        />
        <IntegrationStep
          stepNumber={5}
          title="Audience Creation"
          isCompleted={integrationStatus.audienceCreated}
          icon={faPeopleGroup}
          brandColor="#0081FB"
          details="Enhances campaign creation and dynamic budget allocation, ensuring efficient targeting."
          isLast={true}
        />
      </ol>
    </Eres_card>
  );
};

const Emit_wizard: FC = () => {
  const [currentStep, setCurrentStep] = useState<string>('meta');
  const userIntegrationStatus = checkUserIntegrations();

  const getNextStep = (step: string): string => {
    const steps = ['meta', 'linkedIn', 'tikTok', 'brandCreated', 'audienceCreated'];
    const currentStepIndex = steps.indexOf(step);
    const nextStepIndex = (currentStepIndex + 1) % steps.length;
    return steps[nextStepIndex];
  };

  const handleSkip = () => {
    const nextStep = getNextStep(currentStep);
    setCurrentStep(nextStep);
  };

  const renderFormContent = () => {
    switch (currentStep) {
      case 'meta':
        return (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
            <p>Meta specific content</p>
            <div className="tw-flex tw-gap-2">
                <Eres_wizard_button>Configure Meta</Eres_wizard_button>&nbsp;or&nbsp;
                <Eres_wizard_button onClick={handleSkip}>Skip</Eres_wizard_button>
            </div>
          </div>
        );
      case 'linkedIn':
        return (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
            <p>LinkedIn specific content</p>
            <div className="tw-flex tw-gap-2">
                <Eres_wizard_button>Configure LinkedIn</Eres_wizard_button>&nbsp;or&nbsp;
                <Eres_wizard_button onClick={handleSkip}>Skip</Eres_wizard_button>
            </div>
          </div>
        );
      case 'tikTok':
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
                <p>TikTok specific content</p>
                <div className="tw-flex tw-gap-2">
                    <Eres_wizard_button>Configure TikTok</Eres_wizard_button>&nbsp;or&nbsp;
                    <Eres_wizard_button onClick={handleSkip}>Skip</Eres_wizard_button>
                </div>
            </div>
        );
      case 'brandCreated':
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
                <p>Configure Brand Form</p>
                <div className="tw-flex tw-gap-2">
                    <Eres_wizard_button>Configure Brand</Eres_wizard_button>&nbsp;or&nbsp;
                    <Eres_wizard_button onClick={handleSkip}>Skip</Eres_wizard_button>
                </div>
            </div>
        );
      case 'audienceCreated':
        return (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
            <p>Audience Creation Form</p>
            <div className="tw-flex tw-gap-2">
                <Eres_wizard_button>Configure Audience</Eres_wizard_button>&nbsp;or&nbsp;
                <Eres_wizard_button onClick={handleSkip}>Skip</Eres_wizard_button>
            </div>
          </div>
        );
      default:
        return <div><p>Default content</p></div>;
    }
  };

  return (
    <Fragment>
      <h3 className="tw-text-2xl tw-font-bold tw-py-4">Configuration Wizard</h3>

      <div className="tw-flex tw-flex-col tw-h-screen">
        <div className="tw-flex-grow tw-overflow-auto tw-max-h-[75vh]">
          <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-w-full">
            <UserIntegrationProgress integrationStatus={userIntegrationStatus} />
            <Eres_card>
              {renderFormContent()}
            </Eres_card>
          </div>
        </div>
      </div>
    </Fragment>

  );
};

export default function Wizard({}) {
  return <Emit_main page_id="PAGE-onboarding"><Emit_wizard /></Emit_main>;
}
